import React, { useEffect, useId, useMemo, useState } from "react";

const ITEMS_PER_PAGE = 5;
const MAX_SIZE = 3;

function normaliseBoolean(value) {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    return value.toLowerCase() === "true";
  }

  return Boolean(value);
}

function getVisiblePages(currentPage, totalPages, maxSize) {
  if (totalPages <= maxSize) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const groupIndex = Math.floor((currentPage - 1) / maxSize);
  const startPage = groupIndex * maxSize + 1;
  const endPage = Math.min(startPage + maxSize - 1, totalPages);

  return Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
}

function renderTitle(title) {
  return { __html: title || "" };
}

export default function DocListIsland({ config }) {
  const instanceId = useId();
  const documents = Array.isArray(config.documents) ? config.documents : [];
  const filterOptions = Array.isArray(config.filterOptions) ? config.filterOptions : [];
  const filterText = config.filterText || "Filter By:";
  const showFilter = normaliseBoolean(config.showFilter);
  const allLabel = config.allLabel || "All";
  const previousLabel = config.previousLabel || "Previous";
  const nextLabel = config.nextLabel || "Next";

  const [filterBy, setFilterBy] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const filteredItems = useMemo(() => {
    if (!filterBy) {
      return documents;
    }

    return documents.filter((item) => Array.isArray(item.Categories) && item.Categories.includes(filterBy));
  }, [documents, filterBy]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / ITEMS_PER_PAGE));

  useEffect(() => {
    setCurrentPage(1);
  }, [filterBy]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const paginatedItems = useMemo(() => {
    const firstIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const stopBeforeIndex = Math.min(currentPage * ITEMS_PER_PAGE, filteredItems.length);

    return filteredItems.slice(firstIndex, stopBeforeIndex);
  }, [currentPage, filteredItems]);

  const visiblePages = getVisiblePages(currentPage, totalPages, MAX_SIZE);
  const dropdownId = `doc-list-filter-${instanceId.replace(/:/g, "")}`;

  return (
    <div className="mb-5">
      {showFilter ? (
        <div className="row bg-light p-4 d-flex align-items-center">
          <div className="h3 m-0 mr-3">{filterText}</div>
          <div className="col-lg-4 col-md-6 my-2 p-0">
            <div className="dropdown">
              <button
                className="btn-dropdown dropdown-toggle"
                type="button"
                id={dropdownId}
                data-bs-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                {filterBy || allLabel}
              </button>
              <div className="dropdown-menu" aria-labelledby={dropdownId}>
                <button
                  className={`dropdown-item ${filterBy === "" ? "active" : ""}`}
                  type="button"
                  onClick={() => setFilterBy("")}
                >
                  {allLabel}
                </button>
                {filterOptions.map((item) => (
                  <button
                    key={item}
                    className={`dropdown-item ${item === filterBy ? "active" : ""}`}
                    type="button"
                    onClick={() => setFilterBy(item)}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="list-documents">
        <div className="collapse-group">
          {paginatedItems.map((item, index) => (
            <div key={item.Id || `${item.Url || "doc"}-${index}`}>
              <a href={item.Url} target="_blank" rel="noreferrer">
                <div className="list-icon-left">
                  <svg className="icon doc" width="38" height="50">
                    <use xlinkHref="#doc"></use>
                  </svg>
                </div>
                <div className="list-content mr-3">
                  <div className="h3" dangerouslySetInnerHTML={renderTitle(item.Title)} />
                  <p className="m-0">
                    {item.FileExtension}, {item.NumberOfPages}{" "}
                    <span className="d-block d-md-inline-block">
                      Last Updated {item.LastUpdatedLabel} , {item.FileSizeLabel}
                    </span>
                  </p>
                </div>
              </a>
            </div>
          ))}
        </div>
      </div>

      {filteredItems.length > ITEMS_PER_PAGE ? (
        <ul role="menu" className="pagination ml-auto">
          <li role="menuitem" className={`pagination-prev ${currentPage === 1 ? "disabled" : ""}`}>
            <a
              href="#"
              aria-label={previousLabel}
              aria-disabled={currentPage === 1}
              tabIndex={currentPage === 1 ? -1 : 0}
              onClick={(event) => {
                event.preventDefault();
                if (currentPage > 1) {
                  setCurrentPage(currentPage - 1);
                }
              }}
            >
              {previousLabel}
            </a>
          </li>
          {visiblePages.map((pageNumber) => (
            <li
              key={pageNumber}
              role="menuitem"
              className={`pagination-page ${pageNumber === currentPage ? "active" : ""}`}
            >
              <a
                href="#"
                aria-current={pageNumber === currentPage ? "page" : undefined}
                onClick={(event) => {
                  event.preventDefault();
                  setCurrentPage(pageNumber);
                }}
              >
                {pageNumber}
              </a>
            </li>
          ))}
          <li role="menuitem" className={`pagination-next ${currentPage === totalPages ? "disabled" : ""}`}>
            <a
              href="#"
              aria-label={nextLabel}
              aria-disabled={currentPage === totalPages}
              tabIndex={currentPage === totalPages ? -1 : 0}
              onClick={(event) => {
                event.preventDefault();
                if (currentPage < totalPages) {
                  setCurrentPage(currentPage + 1);
                }
              }}
            >
              {nextLabel}
            </a>
          </li>
        </ul>
      ) : null}
    </div>
  );
}
